import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Droplets, Search, X,
  Building2, Settings2, AlertCircle, TrendingUp, Loader2,
  RotateCcw, Pencil,
} from 'lucide-react';
import { useAppTheme } from '../../contexts/ThemeContext';
import { getWaterUnits, editWaterUnit, updateWaterRates, undoWaterReading } from './api';
import { RateSettingsModal, UndoDialog, EditUnitModal } from './Actions';

// ─── Main Component ───────────────────────────────────────────────────────────
const WaterBills = () => {
  const { palette, Colors } = useAppTheme();

  // Data state
  const [apartments,    setApartments]    = useState([]);
  const [units,         setUnits]         = useState([]);
  const [globalRate,    setGlobalRate]    = useState(0);
  const [loading,       setLoading]       = useState(true);
  const [loadError,     setLoadError]     = useState('');

  // UI state
  const [searchTerm,    setSearchTerm]    = useState('');
  const [showRateModal, setShowRateModal] = useState(false);
  const [editUnit,      setEditUnit]      = useState(null);
  const [undoUnit,      setUndoUnit]      = useState(null);
  const [filters,       setFilters]       = useState({ apartment: '', category: '', arrears: '' });

  // Style tokens
  const bg  = palette.background;
  const txt = palette.text;
  const bdr = palette.border;
  const P   = Colors.primary.main;
  const WARN = Colors.status?.warning || '#F59E0B';
  const card  = { backgroundColor: bg.secondary, border: `1px solid ${bdr}`, borderRadius: '12px' };
  const input = { backgroundColor: bg.tertiary,  border: `1px solid ${bdr}`, color: txt.primary, outline: 'none' };

  // Flatten apartments → categories → units into a flat list for the table
  const flattenUnits = useCallback((aptsData) => {
    const rows = [];
    for (const apt of aptsData) {
      for (const cat of apt.categories) {
        for (const u of cat.units) {
          rows.push({ ...u, apartment: apt.name, apartmentId: apt.id, category: cat.name, categoryId: cat.id });
        }
      }
    }
    return rows;
  }, []);

  // Load all water data
  const loadData = useCallback(async () => {
    setLoading(true); setLoadError('');
    try {
      const data = await getWaterUnits();
      setApartments(data.apartments);
      setGlobalRate(data.globalRate ?? 0);
      setUnits(flattenUnits(data.apartments));
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  }, [flattenUnits]);

  useEffect(() => { loadData(); }, [loadData]);

  // Dynamic filter options from loaded data
  const aptNames = useMemo(() => [...new Set(units.map(u => u.apartment))].sort(), [units]);
  const catNames = useMemo(() => {
    const base = units.filter(u => !filters.apartment || u.apartment === filters.apartment);
    return [...new Set(base.map(u => u.category))].sort();
  }, [units, filters.apartment]);

  // Filtered rows
  const filtered = useMemo(() => units.filter(u => {
    if (searchTerm && !u.unit.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !u.tenant.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filters.apartment && u.apartment !== filters.apartment) return false;
    if (filters.category  && u.category  !== filters.category)  return false;
    if (filters.arrears === 'with'    && !(u.arrears > 0)) return false;
    if (filters.arrears === 'without' && u.arrears > 0)    return false;
    return true;
  }), [units, searchTerm, filters]);

  // Summary stats from filtered rows
  const stats = useMemo(() => {
    const occupied         = filtered.filter(u => u.tenant !== 'Vacant');
    const totalConsumption = filtered.reduce((s, u) => s + (u.consumption ?? 0), 0);
    const totalBilled      = occupied.reduce((s, u) => s + (u.billAmount  ?? 0), 0);
    const totalArrears     = occupied.reduce((s, u) => s + (u.arrears     ?? 0), 0);
    return { occupied: occupied.length, totalConsumption, totalBilled, totalArrears };
  }, [filtered]);

  // ── Undo handler ─────────────────────────────────────────────────────────────
  const handleUndoConfirm = async (unitId) => {
    const data = await undoWaterReading(unitId);
    setUnits(prev => prev.map(u => {
      if (u.unitId !== unitId) return u;
      return {
        ...u,
        currReading:  data.currReading,    // now equals prevReading
        consumption:  0,
        billAmount:   0,
        arrears:      data.arrears ?? u.arrears,
      };
    }));
    setUndoUnit(null);
  };

  // ── Edit / correction handler ────────────────────────────────────────────────
  const handleUnitEditSave = async (unitId, payload) => {
    const data = await editWaterUnit(unitId, payload);
    setUnits(prev => prev.map(u => {
      if (u.unitId !== unitId) return u;
      return {
        ...u,
        prevReading: data.prevReading ?? u.prevReading,
        currReading: data.currReading ?? u.currReading,
        rate:        data.rate        ?? u.rate,
        consumption: data.consumption ?? u.consumption,
        billAmount:  data.billAmount  ?? u.billAmount,
        arrears:     data.arrears     ?? u.arrears,
      };
    }));
    setEditUnit(null);
  };

  // ── Rate handler ─────────────────────────────────────────────────────────────
  const handleRateSave = async (payload) => {
    await updateWaterRates(payload);
    await loadData(); // reload so all rows reflect new rates
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length + (searchTerm ? 1 : 0);
  const ARREARS_LABELS    = { with: 'With Arrears', without: 'No Arrears' };

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg.primary }}>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="px-6 pt-6 pb-4">
        <div className="rounded-xl p-6" style={card}>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: P }}>Water Bills</h1>
              <p className="mt-1" style={{ color: txt.secondary }}>Manage readings, rates &amp; billing for all units</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.7rem', color: txt.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Global Rate</div>
                <div style={{ fontSize: '1.05rem', fontWeight: 800, color: P }}>KES {globalRate}/m³</div>
              </div>
              <button
                onClick={() => setShowRateModal(true)}
                className="text-white font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2"
                style={{ backgroundColor: P }}>
                <Settings2 className="w-4 h-4" /> Modify Rates
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Summary Cards ───────────────────────────────────────────────────── */}
      <div className="px-6 pb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Occupied Units',    value: stats.occupied,                                  sub: `of ${units.length} total`, icon: <Building2 className="w-5 h-5" />, color: Colors.accent?.blue || '#3B82F6' },
            { label: 'Total Consumption', value: `${stats.totalConsumption.toLocaleString()} m³`, sub: 'this billing period',      icon: <Droplets className="w-5 h-5" />,  color: P },
            { label: 'Total Billed',      value: `KES ${stats.totalBilled.toLocaleString()}`,     sub: 'current period',           icon: <TrendingUp className="w-5 h-5" />, color: Colors.status?.success || '#10B981' },
            { label: 'Total Arrears', value: `KES -${stats.totalArrears.toLocaleString()}`, sub: 'outstanding balance', icon: <AlertCircle className="w-5 h-5" />, color: Colors.status?.error || '#EF4444' },
          ].map(s => (
            <div key={s.label} className="rounded-xl p-5 flex items-start gap-3" style={card}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${s.color}18`, color: s.color }}>{s.icon}</div>
              <div>
                <div className="text-xl font-extrabold" style={{ color: txt.primary }}>{s.value}</div>
                <div className="text-xs font-semibold mt-0.5" style={{ color: txt.secondary }}>{s.label}</div>
                <div className="text-xs" style={{ color: txt.muted }}>{s.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Search & Filters ────────────────────────────────────────────────── */}
      <div className="px-6 pb-4">
        <div className="rounded-xl p-4" style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {/* Search */}
            <div style={{ position: 'relative', flex: '0 0 220px', minWidth: 160 }}>
              <div style={{ position: 'absolute', inset: 0, left: 0, paddingLeft: 12, display: 'flex', alignItems: 'center', pointerEvents: 'none' }}>
                <Search className="w-4 h-4" style={{ color: txt.muted }} />
              </div>
              <input type="text" placeholder="Search unit or tenant…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                style={{ ...input, paddingLeft: 36, paddingRight: 12, paddingTop: 9, paddingBottom: 9, borderRadius: 8, fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' }} />
            </div>

            {/* Apartment */}
            <select value={filters.apartment} onChange={e => setFilters({ ...filters, apartment: e.target.value, category: '' })}
              style={{ ...input, padding: '9px 12px', borderRadius: 8, fontSize: '0.85rem', flex: '1 1 140px', minWidth: 130, maxWidth: 180 }}>
              <option value="">All Apartments</option>
              {aptNames.map(a => <option key={a} value={a}>{a}</option>)}
            </select>

            {/* Category */}
            <select value={filters.category} onChange={e => setFilters({ ...filters, category: e.target.value })}
              style={{ ...input, padding: '9px 12px', borderRadius: 8, fontSize: '0.85rem', flex: '1 1 130px', minWidth: 120, maxWidth: 160 }}>
              <option value="">All Categories</option>
              {catNames.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            {/* Arrears */}
            <select value={filters.arrears} onChange={e => setFilters({ ...filters, arrears: e.target.value })}
              style={{ ...input, padding: '9px 12px', borderRadius: 8, fontSize: '0.85rem', flex: '1 1 120px', minWidth: 110, maxWidth: 150 }}>
              <option value="">All Balances</option>
              <option value="with">With Arrears</option>
              <option value="without">No Arrears</option>
            </select>

            {activeFilterCount > 0 && (
              <button onClick={() => { setFilters({ apartment: '', category: '', arrears: '' }); setSearchTerm(''); }}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '9px 14px', borderRadius: 8, border: `1px solid ${bdr}`, backgroundColor: bg.tertiary, color: txt.secondary, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                <X className="w-3.5 h-3.5" /> Clear ({activeFilterCount})
              </button>
            )}
          </div>

          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2 flex-wrap mt-3 pt-3" style={{ borderTop: `1px solid ${bdr}` }}>
              <span className="text-xs font-semibold" style={{ color: txt.secondary }}>Active:</span>
              {searchTerm && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full font-medium" style={{ backgroundColor: `${P}15`, color: P }}>
                  &quot;{searchTerm}&quot;
                  <button onClick={() => setSearchTerm('')} className="rounded-full p-0.5"><X className="w-3 h-3" /></button>
                </span>
              )}
              {Object.entries(filters).map(([key, val]) => val && (
                <span key={key} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full font-medium" style={{ backgroundColor: `${P}15`, color: P }}>
                  {key === 'arrears' ? ARREARS_LABELS[val] : val}
                  <button onClick={() => setFilters({ ...filters, [key]: '' })} className="rounded-full p-0.5"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Count */}
      <div className="px-6 pb-4">
        <p className="text-sm font-medium" style={{ color: txt.secondary }}>
          Showing <span className="font-bold" style={{ color: P }}>{filtered.length}</span> of <span className="font-bold">{units.length}</span> units
        </p>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────────── */}
      <div className="px-6 pb-6">
        <div className="rounded-xl overflow-hidden" style={card}>
          <div className="overflow-x-auto">
            {loading ? (
              <div style={{ padding: '64px 24px', textAlign: 'center', color: txt.muted }}>
                <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin" style={{ color: P }} />
                <div className="text-sm">Loading water data…</div>
              </div>
            ) : loadError ? (
              <div style={{ padding: '64px 24px', textAlign: 'center', color: Colors.status?.error || '#EF4444' }}>
                <AlertCircle className="w-8 h-8 mx-auto mb-3 opacity-60" />
                <div className="text-sm mb-3">{loadError}</div>
                <button onClick={loadData} style={{ padding: '7px 18px', borderRadius: 8, border: `1px solid ${bdr}`, backgroundColor: 'transparent', color: txt.secondary, cursor: 'pointer', fontSize: '0.85rem' }}>
                  Retry
                </button>
              </div>
            ) : (
              <table className="w-full">
                <thead style={{ backgroundColor: bg.tertiary, borderBottom: `1px solid ${bdr}` }}>
                  <tr>
                    {['Unit / Tenant', 'Previous Reading', 'Current Reading', 'Consumption', 'Bill Amount', 'Total Arrears', 'Actions'].map(col => (
                      <th key={col} className="px-6 py-4 text-left text-xs font-bold uppercase" style={{ color: txt.secondary }}>
                        {col}
                        {(col === 'Consumption' || col === 'Bill Amount') && (
                          <div style={{ fontSize: '0.65rem', fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: txt.muted, marginTop: 1 }}>recent cycle</div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-16 text-center" style={{ color: txt.muted }}>
                        <Droplets className="w-8 h-8 mx-auto mb-3 opacity-30" />
                        <div className="text-sm">No units match your search.</div>
                      </td>
                    </tr>
                  ) : filtered.map(u => {
                    const isVacant   = u.tenant === 'Vacant';
                    const hasArrears = (u.arrears ?? 0) > 0;
                    // "Undone" = curr == prev (recording was reversed; edit will unlock)
                    const isUndone   = !isVacant && Number(u.currReading) === Number(u.prevReading);
                    // Undo button is disabled if already undone (nothing to reverse)
                    const canUndo    = !isVacant && !isUndone && (u.billAmount ?? 0) > 0;

                    return (
                      <tr key={u.unitId}
                        style={{ borderBottom: `1px solid ${bdr}`, opacity: isVacant ? 0.6 : 1 }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = bg.tertiary}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>

                        {/* Unit / Tenant */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xs font-extrabold flex-shrink-0"
                              style={{ backgroundColor: isVacant ? `${txt.muted}18` : `${P}18`, color: isVacant ? txt.muted : P }}>
                              {u.unit}
                            </div>
                            <div>
                              <div className="font-semibold text-sm" style={{ color: txt.primary }}>{u.unit}</div>
                              <div className="text-xs" style={{ color: isVacant ? txt.muted : txt.secondary, fontStyle: isVacant ? 'italic' : 'normal' }}>{u.tenant}</div>
                              <div className="text-xs" style={{ color: txt.muted }}>{u.apartment} · {u.category}</div>
                            </div>
                          </div>
                        </td>

                        {/* Previous Reading */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-semibold text-sm" style={{ color: txt.primary }}>
                            {u.prevReading != null ? `${Number(u.prevReading).toLocaleString()} m³` : '—'}
                          </div>
                          {u.prevDate && <div className="text-xs" style={{ color: txt.muted }}>{new Date(u.prevDate).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' })}</div>}
                        </td>

                        {/* Current Reading */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-semibold text-sm" style={{ color: txt.primary }}>
                            {u.currReading != null ? `${Number(u.currReading).toLocaleString()} m³` : '—'}
                          </div>
                          {u.currDate && <div className="text-xs" style={{ color: txt.muted }}>{new Date(u.currDate).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' })}</div>}
                          {/* Badge shown when reading has been undone */}
                          {isUndone && (
                            <div className="text-xs mt-0.5 font-semibold" style={{ color: WARN }}>
                              Undone — awaiting correction
                            </div>
                          )}
                        </td>

                        {/* Consumption */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-bold rounded-full"
                            style={{ backgroundColor: (u.consumption ?? 0) > 0 ? `${P}15` : `${txt.muted}18`, color: (u.consumption ?? 0) > 0 ? P : txt.muted }}>
                            <Droplets className="w-3 h-3" />
                            {u.consumption ?? 0} m³
                          </span>
                        </td>

                        {/* Bill Amount */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-bold" style={{ color: isVacant ? txt.muted : txt.primary }}>
                            {isVacant ? '—' : `KES ${(u.billAmount ?? 0).toLocaleString()}`}
                          </span>
                        </td>

                        {/* Arrears */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isVacant ? (
                            <span style={{ color: txt.muted, fontSize: '0.875rem' }}>—</span>
                          ) : hasArrears ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-bold rounded-full"
                              style={{ backgroundColor: `${Colors.status?.error || '#EF4444'}15`, color: Colors.status?.error || '#EF4444' }}>
                              <AlertCircle className="w-3 h-3" />
                              −KES {(u.arrears ?? 0).toLocaleString()}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-bold rounded-full"
                              style={{ backgroundColor: `${Colors.status?.success || '#10B981'}15`, color: Colors.status?.success || '#10B981' }}>
                              Clear
                            </span>
                          )}
                        </td>

                        {/* Actions — Undo + Edit */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>

                            {/* Undo — only for occupied units with an active recording */}
                            {!isVacant && (
                              <button
                                onClick={() => setUndoUnit(u)}
                                disabled={!canUndo}
                                title={!canUndo ? 'Nothing to undo' : 'Undo this recording'}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold"
                                style={{
                                  border: `1px solid ${canUndo ? WARN : bdr}`,
                                  backgroundColor: 'transparent',
                                  color: canUndo ? WARN : txt.muted,
                                  cursor: canUndo ? 'pointer' : 'not-allowed',
                                  opacity: canUndo ? 1 : 0.5,
                                  transition: 'all 0.15s',
                                }}
                                onMouseEnter={e => { if (canUndo) e.currentTarget.style.backgroundColor = `${WARN}15`; }}
                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                                <RotateCcw className="w-3 h-3" /> Undo
                              </button>
                            )}

                            {/* Edit */}
                            <button
                              onClick={() => setEditUnit(u)}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold"
                              style={{ border: 'none', backgroundColor: P, color: '#fff', cursor: 'pointer' }}
                              onMouseEnter={e => { e.currentTarget.style.opacity = '0.82'; }}
                              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}>
                              <Pencil className="w-3 h-3" /> Edit
                            </button>

                          </div>
                        </td>

                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Rate Modal */}
      {showRateModal && (
        <RateSettingsModal
          onClose={() => setShowRateModal(false)}
          onSave={handleRateSave}
          globalRate={globalRate}
          apartments={apartments}
          palette={palette}
          Colors={Colors}
        />
      )}

      {/* Undo Dialog */}
      {undoUnit && (
        <UndoDialog
          unit={undoUnit}
          onClose={() => setUndoUnit(null)}
          onConfirm={handleUndoConfirm}
          palette={palette}
          Colors={Colors}
        />
      )}

      {/* Edit / Correction Modal */}
      {editUnit && (
        <EditUnitModal
          unit={editUnit}
          onClose={() => setEditUnit(null)}
          onSave={handleUnitEditSave}
          palette={palette}
          Colors={Colors}
        />
      )}
    </div>
  );
};

export default WaterBills;